import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import dayjs, { DATE_FORMATS } from '../../../utils/dateUtils';

import CommonModal from '../../../components/common-modal/CommonModal';
import TooltipButton from '../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import SingleUploadZone from './single-upload-zone/SingleUploadZone';
import MediaGrid from './media-grid/MediaGrid';
import callApi from '../../../service/callApi';
import { MEDIA_CONVERT_STATUS, MEDIA_TYPES, MEDIA_UPLOAD_STATUS } from '../../../constants';
import asyncPool from '../../../service/asyncPool';
import { enqueueSnackbar } from '../../../redux/notifier/notifierActions';
import { useProject } from '../../../context/ProjectContext';
import ErrorList from './components/error-list/ErrorList';
import sendRequest from '../../../service/sendRequest';

import scss from './MultipleUploadMedia.module.scss';

const CONCURRENCY = 3;

function MultipleUploadMedia({ isOpen, onClose }) {
  const { t } = useTranslation('activ8');
  const dispatch = useDispatch();
  const { actions } = useProject();

  const gridRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [duplicates, setDuplicates] = useState({});

  const { id: projectId } = useParams();

  const itemsToFindDuplicates = useMemo(
    () => mediaItems.filter((x) => duplicates[x.id] === undefined),
    [mediaItems, duplicates],
  );

  const itemsToConvert = useMemo(
    () => mediaItems.filter((x) => !!duplicates[x.id] && x.convertStatus === MEDIA_CONVERT_STATUS.PENDING),
    [mediaItems, duplicates],
  );

  const handleUploadMediaItems = (newITems) => {
    gridRef.current?.beginLayoutBatch();

    setMediaItems((prev) => [...prev, ...newITems]);

    requestAnimationFrame(() => {
      gridRef.current?.endLayoutBatch();
    });
  };

  const patchItem = (id, patch) => {
    setMediaItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  const handleProgress = (id, loaded, total) => {
    const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;

    patchItem(id, {
      status: MEDIA_UPLOAD_STATUS.UPLOADING,
      uploadedBytes: loaded,
      progress: progress,
    });
  };

  const handleError = (id) => {
    patchItem(id, {
      status: MEDIA_UPLOAD_STATUS.FAILED,
    });
  };

  const handleShowError = () => {
    dispatch(
      enqueueSnackbar({
        message: 'someFilesFailedToUpload',
        options: {
          variant: 'error',
          subMessage: 'youCanTryAgainOrRemoveThem',
        },
      }),
    );
  };

  const handleClose = () => {
    if (mediaItems.length) {
      mediaItems.forEach((item) => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
      setMediaItems([]);
    }
    setUploadErrors([]);
    setDuplicates({});
    onClose();
  };

  const handleCloseErrorList = () => {
    setUploadErrors([]);
  };

  const handleLoadingFinished = () => {
    if (mediaItems.length && mediaItems.every((x) => x.status === MEDIA_UPLOAD_STATUS.SUCCEEDED)) {
      actions('searchContent', { id: projectId });
      handleClose();
    } else if (mediaItems.length && mediaItems.some((x) => x.status === MEDIA_UPLOAD_STATUS.FAILED)) {
      handleShowError();
    }
  };

  const handleMoveItemToError = ({ id, file }) => {
    setMediaItems((prev) => prev.filter((x) => x.id !== id));
    setUploadErrors((prev) => [...prev, { id, file, errors: ['invalid-file'] }]);
  };

  const handleFindDuplicates = async () => {
    setDuplicates((prev) => {
      const next = { ...prev };
      itemsToFindDuplicates.forEach((item) => {
        if (next[item.id] === undefined) next[item.id] = null;
      });
      return next;
    });

    await asyncPool(CONCURRENCY, itemsToFindDuplicates, async (item) => {
      const response = await actions('getDuplicates', { projectId: projectId, media: item.file });
      setDuplicates((prev) => ({ ...prev, [item.id]: response.payload ?? [] }));
    });
  };

  const handleConvertMedia = async () => {
    setMediaItems((prev) =>
      prev.map((x) =>
        itemsToConvert.some((y) => y.id === x.id) ? { ...x, convertStatus: MEDIA_CONVERT_STATUS.CONVERTING } : x,
      ),
    );

    await asyncPool(CONCURRENCY, itemsToConvert, async (item) => {
      const form = new FormData();
      form.append('media', item.file);

      const response = await sendRequest(
        () =>
          callApi.post(`/api/activ8/media/convert?projectId=${projectId}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'arraybuffer',
          }),
        {
          onError: () => {
            handleMoveItemToError({ id: item.id, file: item.file });
          },
        },
      );

      URL.revokeObjectURL(item.preview);

      const buffer =
        response.data instanceof ArrayBuffer
          ? response.data
          : response.data.buffer ?? new Uint8Array(response.data).buffer;
      const { mediaType, mimeType } = getConvertedMediaType(buffer);
      const preview = URL.createObjectURL(new Blob([response.data], { type: mimeType }));

      patchItem(item.id, {
        convertStatus: MEDIA_CONVERT_STATUS.CONVERTED,
        preview,
        type: mediaType,
      });
    });
  };

  const handleClearDuplicates = (id) => {
    setDuplicates((prev) => ({ ...prev, [id]: [] }));
  };

  const handleFailedMediaLoading = ({ id }) => {
    patchItem(id, {
      convertStatus: MEDIA_CONVERT_STATUS.PENDING,
      type: MEDIA_TYPES.APPLICATION,
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    const itemsToUpload = mediaItems.filter((x) => x.status !== MEDIA_UPLOAD_STATUS.SUCCEEDED);

    await asyncPool(CONCURRENCY, itemsToUpload, async (item) => {
      patchItem(item.id, { status: MEDIA_UPLOAD_STATUS.UPLOADING, progress: 0 });

      try {
        await uploadSingle({ item, onProgress: handleProgress, projectId });

        patchItem(item.id, {
          status: MEDIA_UPLOAD_STATUS.SUCCEEDED,
          progress: 100,
        });
      } catch (e) {
        handleError(item.id);
      }
    });

    setIsLoading(false);
    handleLoadingFinished();
  };

  const handleClearModal = () => {
    setMediaItems([]);
    setUploadErrors([]);
    setDuplicates({});
    if (mediaItems.length) {
      mediaItems.forEach((item) => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
      setMediaItems([]);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      handleLoadingFinished();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isOpen) {
      handleClearModal();
    } else {
      actions('searchTags', { id: projectId, query: '' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (itemsToFindDuplicates.length) {
      handleFindDuplicates();
    }
  }, [itemsToFindDuplicates]);

  useEffect(() => {
    if (itemsToConvert.length) {
      handleConvertMedia();
    }
  }, [itemsToConvert]);

  return (
    <CommonModal isOpen={isOpen} onClose={handleClose} isAnimated wrapperClassName={scss.modalWrapper}>
      {!mediaItems.length ? (
        <SingleUploadZone
          onUploadMediaItems={handleUploadMediaItems}
          disabled={isLoading}
          onChangeErrors={setUploadErrors}
        />
      ) : null}
      {/* {mediaItems.length ? (
        <MediaGrid
          mediaItems={mediaItems}
          onChangeMediaItems={setMediaItems}
          duplicates={duplicates}
          onClearDuplicates={handleClearDuplicates}
          onChangeErrors={setUploadErrors}
          onPatchItem={patchItem}
        />
      ) : null} */}
      {mediaItems.length ? (
        <MediaGrid
          ref={gridRef}
          mediaItems={mediaItems}
          onUploadMediaItems={handleUploadMediaItems}
          onChangeMediaItems={setMediaItems}
          duplicates={duplicates}
          onClearDuplicates={handleClearDuplicates}
          onChangeErrors={setUploadErrors}
          onMediaErrorLoading={handleFailedMediaLoading}
        />
      ) : null}

      {uploadErrors.length ? <ErrorList errors={uploadErrors} onClose={handleCloseErrorList} /> : null}

      <div className={scss.actions}>
        <TooltipButton onClick={handleClose} styleColor={BUTTON_STYLES.SECONDARY} maxWidth={144} disabled={isLoading}>
          {t('cancel')}
        </TooltipButton>
        <TooltipButton onClick={handleSubmit} maxWidth={144} isLoading={isLoading}>
          {t('submit')}
        </TooltipButton>
      </div>
    </CommonModal>
  );
}

export default MultipleUploadMedia;

async function uploadSingle({ item, onProgress, projectId }) {
  const form = new FormData();
  form.append('media', item.file);
  form.append('projectId', projectId);
  form.append('tags', item.tags);
  form.append('thirdPartyRights', String(item.thirdPartyRights ?? false));
  if (item.expirationDate) {
    form.append('expirationDate', dayjs(item.expirationDate).format(DATE_FORMATS.API_DATE));
  }

  const res = await callApi.post('/api/activ8/media/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      const total = e.total ?? item.size ?? 0;
      onProgress(item.id, e.loaded, total);
    },
  });

  return res.data;
}

function getConvertedMediaType(buffer) {
  const bytes = new Uint8Array(buffer);
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  const isPng = pngSignature.every((b, i) => bytes[i] === b);
  if (isPng) return { mediaType: MEDIA_TYPES.IMAGE, mimeType: 'image/png' };
  const ftyp = bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
  if (ftyp) return { mediaType: MEDIA_TYPES.VIDEO, mimeType: 'video/mp4' };
  return { mediaType: MEDIA_TYPES.IMAGE, mimeType: 'image/png' };
}
